import { Check, ChevronsUpDown, X } from "lucide-react"
import * as React from "react"

import { Badge } from "@/components/ui/badge"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface Option {
    label: string
    value: string | number
    id?: string | number // Optional ID if value is not unique or needed separately
}

interface MultiSelectProps {
    options: Option[]
    selected: (string | number)[] // Array of values
    onChange: (values: (string | number)[]) => void
    placeholder?: string
    className?: string
    badgeClassName?: string
    emptyText?: string
    isLoading?: boolean
}

export function MultiSelect({
    options,
    selected,
    onChange,
    placeholder = "Select items...",
    className,
    badgeClassName,
    emptyText = "No item found.",
    isLoading = false
}: MultiSelectProps) {
    const [open, setOpen] = React.useState(false)

    const selectedOptions = options.filter(option => selected.includes(option.value))

    const handleUnselect = (value: string | number) => {
        onChange(selected.filter((item) => item !== value))
    }

    const handleSelect = (value: string | number) => {
        if (selected.includes(value)) {
            handleUnselect(value)
        } else {
            onChange([...selected, value])
        }
        // Don't close for multi-select
    }

    return (
        <Popover open={open} onOpenChange={setOpen} modal={true}>
            <PopoverTrigger asChild>
                <div
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "flex min-h-11 w-full flex-wrap items-center justify-between rounded-xl border border-muted-foreground/10 bg-muted/30 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer hover:bg-muted/40 transition-colors shadow-sm",
                        className
                    )}
                >
                    <div className="flex flex-wrap gap-1.5 flex-1">
                        {selectedOptions.length > 0 ? (
                            selectedOptions.map((option) => (
                                <Badge
                                    key={option.value}
                                    variant="secondary"
                                    className={cn("rounded-lg bg-background/50 hover:bg-background/80 border border-border/10 px-2 py-1 font-normal", badgeClassName)}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        // Optional: clicking badge could do something?
                                    }}
                                >
                                    {option.label}
                                    <div
                                        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer hover:bg-destructive/20 hover:text-destructive p-0.5 transition-colors"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleUnselect(option.value)
                                            }
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleUnselect(option.value)
                                        }}
                                    >
                                        <X className="h-3 w-3" />
                                    </div>
                                </Badge>
                            ))
                        ) : (
                            <span className="text-muted-foreground opacity-50">{isLoading ? "Loading..." : placeholder}</span>
                        )}
                    </div>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 rounded-xl border-border/60 shadow-xl bg-background/95 backdrop-blur-xl" align="start">
                <Command>
                    <CommandInput placeholder="Search..." />
                    <CommandList className="max-h-[250px] overflow-auto">
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = selected.includes(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label} // Use label for filtering
                                        onSelect={() => {
                                            handleSelect(option.value)
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <div
                                            className={cn(
                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                isSelected
                                                    ? "bg-primary text-primary-foreground"
                                                    : "opacity-50 [&_svg]:invisible"
                                            )}
                                        >
                                            <Check className={cn("h-4 w-4")} />
                                        </div>
                                        {option.label}
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
